import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'
import classnames from 'classnames'
import { Button, FeatherLoader } from '@abroad/components'

const ScheduleDatePicker = ({
  currentMonth,
  selectedDate,
  selectedDates,
  onMonthChange,
  onDateSelect,
  onDatesChange,
  availableDays = {},
  multiple = false,
  disablePastDates = false,
  isLoading = false,
  disableDatesWithoutSlots = false,
  // Preview only: keep days without slots clickable (past days stay disabled)
  allowDaysWithoutSlots = false,
  unavailableDates = [],
  restrictionDates = [],
}) => {
  const monthMoment = moment(currentMonth || undefined).startOf('month')

  const startOfMonth = monthMoment.clone().startOf('month')
  const endOfMonth = monthMoment.clone().endOf('month')
  const startDate = startOfMonth.clone().startOf('week')
  const endDate = endOfMonth.clone().endOf('week')

  const days = []
  let cursor = startDate.clone()
  while (cursor.isSameOrBefore(endDate, 'day')) {
    days.push(cursor.clone())
    cursor.add(1, 'day')
  }

  const weekDays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']

  // Normalize selectedDates to array of date strings
  const selectedDatesArray = useMemo(() => {
    if (multiple) {
      if (!selectedDates) return []
      return selectedDates.map((date) => moment(date).format('YYYY-MM-DD'))
    }
    // Single selection mode: check selectedDate first, then selectedDates array
    if (selectedDate) {
      return [moment(selectedDate).format('YYYY-MM-DD')]
    }
    if (selectedDates && selectedDates.length > 0) {
      return [moment(selectedDates[0]).format('YYYY-MM-DD')]
    }
    return []
  }, [multiple, selectedDate, selectedDates])

  const selectedDatesSet = useMemo(
    () => new Set(selectedDatesArray),
    [selectedDatesArray],
  )

  const isSelected = (date) => {
    const dateStr = date.format('YYYY-MM-DD')
    return selectedDatesSet.has(dateStr)
  }

  const isCurrentMonth = (date) => date.isSame(monthMoment, 'month')
  const isToday = (date) => date.isSame(moment(), 'day')

  // A day the API returned without slots (e.g. fully booked); only meaningful when allowDaysWithoutSlots is on
  const hasNoSlots = (date) =>
    allowDaysWithoutSlots &&
    Object.keys(availableDays).length > 0 &&
    !(availableDays[date.format('YYYY-MM-DD')]?.slots?.length > 0)

  const isDayDisabled = (date) => {
    // Check if date is in the past (if disablePastDates is true)
    if (disablePastDates && date.isBefore(moment(), 'day')) {
      return true
    }

    if (allowDaysWithoutSlots) {
      return false
    }

    // If disableDatesWithoutSlots is enabled and API has been called (not loading),
    // check if date has available slots
    const dateStr = date.format('YYYY-MM-DD')

    if (
      unavailableDates.includes(dateStr) ||
      restrictionDates.includes(dateStr)
    ) {
      return false
    }

    if (disableDatesWithoutSlots && !isLoading) {
      const dayMeta = availableDays[dateStr]
      const hasSlots = Array.isArray(dayMeta?.slots) && dayMeta.slots.length > 0
      return !hasSlots
    }

    // Legacy behavior: only check if availableDays has entries
    if (Object.keys(availableDays).length > 0) {
      const dayMeta = availableDays[dateStr]
      const hasSlots = Array.isArray(dayMeta?.slots) && dayMeta.slots.length > 0
      return !hasSlots
    }

    return false
  }

  const handlePrevMonth = () => {
    if (!onMonthChange) return
    onMonthChange(monthMoment.clone().subtract(1, 'month'))
  }

  const handleNextMonth = () => {
    if (!onMonthChange) return
    onMonthChange(monthMoment.clone().add(1, 'month'))
  }

  const handleDateClick = (date) => {
    const dateStr = date.format('YYYY-MM-DD')

    if (multiple && onDatesChange) {
      // Toggle selection for multiple mode
      const newSelectedDates = [...selectedDatesArray]
      const index = newSelectedDates.indexOf(dateStr)
      if (index > -1) {
        // Remove if already selected
        newSelectedDates.splice(index, 1)
      } else {
        // Add if not selected
        newSelectedDates.push(dateStr)
      }
      // Convert back to moment objects or ISO strings for callback
      onDatesChange(newSelectedDates.map((d) => moment(d).toISOString()))
    } else if (!multiple && onDateSelect) {
      // Single selection mode
      onDateSelect(dateStr)
    }
  }

  const handleTodayClick = () => {
    const today = moment()
    const todayStr = today.format('YYYY-MM-DD')

    // Navigate to current month if not already viewing it
    if (!today.isSame(monthMoment, 'month') && onMonthChange) {
      onMonthChange(today.clone().startOf('month'))
    }

    // Check if today is disabled
    if (isDayDisabled(today)) {
      return
    }

    if (multiple && onDatesChange) {
      // Toggle selection for multiple mode
      const newSelectedDates = [...selectedDatesArray]
      const index = newSelectedDates.indexOf(todayStr)
      if (index > -1) {
        // Remove if already selected
        newSelectedDates.splice(index, 1)
      } else {
        // Add if not selected
        newSelectedDates.push(todayStr)
      }
      // Convert back to moment objects or ISO strings for callback
      onDatesChange(newSelectedDates.map((d) => moment(d).toISOString()))
    } else if (!multiple && onDateSelect) {
      // Single selection mode
      onDateSelect(todayStr)
    }
  }

  return (
    <div className='session-date-picker h-100'>
      <div className='session-date-picker__header'>
        <span className='sa4 font-16px font-weight-bold'>
          {monthMoment.format('MMMM YYYY')}
        </span>
        <div className='session-date-picker__navigation'>
          <Button
            variant='none'
            className='navTabs mr-3'
            onClick={handleTodayClick}
            disabled={isLoading}
            aria-label='Select today'>
            Today
          </Button>
          <Button
            variant='none'
            className='p-0 mr-4 border-0 box-shadow-none'
            onClick={handlePrevMonth}
            aria-label='Previous month'>
            <svg
              width='11'
              height='19'
              viewBox='0 0 11 19'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M9.49615 1.25L1.25 9.25003'
                stroke='black'
                strokeWidth='2.5'
                strokeLinecap='round'
              />
              <path
                d='M9.49615 17.25L1.25 9.25'
                stroke='black'
                strokeWidth='2.5'
                strokeLinecap='round'
              />
            </svg>
          </Button>
          <Button
            variant='none'
            className='p-0 ml-3 border-0 box-shadow-none'
            onClick={handleNextMonth}
            aria-label='Next month'>
            <svg
              width='11'
              height='19'
              viewBox='0 0 11 19'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M1.24994 1.25L9.49609 9.25003'
                stroke='black'
                strokeWidth='2.5'
                strokeLinecap='round'
              />
              <path
                d='M1.24994 17.25L9.49609 9.25'
                stroke='black'
                strokeWidth='2.5'
                strokeLinecap='round'
              />
            </svg>
          </Button>
        </div>
      </div>
      <div className='session-date-picker__weekdays mt-3'>
        {weekDays.map((day) => (
          <div key={day} className='sa5 text-dark text-center'>
            {day}
          </div>
        ))}
      </div>
      {isLoading ? (
        <div className='session-date-picker__days'>
          <div
            className='d-flex justify-content-center align-items-center'
            style={{
              gridColumn: '1 / -1',
              minHeight: '420px',
            }}>
            <FeatherLoader />
          </div>
        </div>
      ) : (
        <div className='session-date-picker__days'>
          {days.map((day, index) => {
            const dateStr = day.format('YYYY-MM-DD')
            const selected = isSelected(day)
            const currentMonthDay = isCurrentMonth(day)
            const today = isToday(day)
            const dayDisabled = !currentMonthDay || isDayDisabled(day)
            const isUnavailable = unavailableDates.includes(dateStr)
            const isRestriction = restrictionDates.includes(dateStr)

            return (
              <button
                key={dateStr || index}
                type='button'
                className={classnames('session-date-picker__day', {
                  'session-date-picker__day--selected': selected,
                  'session-date-picker__day--other-month': !currentMonthDay,
                  'session-date-picker__day--today': today && !selected,
                  'session-date-picker__day--disabled': dayDisabled,
                  'session-date-picker__day--no-slots':
                    currentMonthDay && hasNoSlots(day),
                  'session-date-picker__day--unavailable': isUnavailable,
                  'session-date-picker__day--restriction': isRestriction,
                })}
                onClick={() => !dayDisabled && handleDateClick(day)}
                disabled={dayDisabled}>
                {day.format('D')}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

ScheduleDatePicker.propTypes = {
  currentMonth: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
    PropTypes.instanceOf(moment),
  ]),
  selectedDate: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
    PropTypes.instanceOf(moment),
  ]),
  selectedDates: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.string,
      PropTypes.instanceOf(moment),
    ]),
  ),
  onMonthChange: PropTypes.func,
  onDateSelect: PropTypes.func,
  onDatesChange: PropTypes.func,
  availableDays: PropTypes.object,
  multiple: PropTypes.bool,
  disablePastDates: PropTypes.bool,
  isLoading: PropTypes.bool,
  disableDatesWithoutSlots: PropTypes.bool,
  unavailableDates: PropTypes.arrayOf(PropTypes.string),
  restrictionDates: PropTypes.arrayOf(PropTypes.string),
}

export default ScheduleDatePicker
